<template>
  <BlokkliProvider
    v-if="node"
    v-slot="{ isEditing }"
    v-bind="node?.blokkliProps"
    isolate
    class="mb-64 md:mb-112"
  >
    <div
      ref="container"
      class="m-auto flex min-h-[500px] max-w-[2500px] flex-col overflow-hidden md:flex-row"
      :class="{ 'h-screen overflow-hidden': isEditing }"
    >
      <div
        class="w-full shrink-0 overflow-y-auto md:w-[55%] md:pl-[73px] md:pr-32 lg:w-[35.5%]"
      >
        <div class="my-24 md:my-80 md:pr-24">
          <h1
            v-if="node"
            class="typo-headline-2 mb-24 pl-[15px] uppercase"
            :class="{
              'font-bold': isRebranding,
            }"
          >
            {{ node.headline }}
          </h1>
          <div class="typo-body-2 mb-56 text-grey-dark-01 mobile-only:hidden">
            {{
              $texts(
                'storefinder.intro',
                'We suggest you experience our watches on site or have your watches serviced by visiting us spontaneously in one of our beautiful stores.',
              )
            }}
          </div>
          <div class="flex flex-col">
            <div
              class="order-last bg-grey-light-03 p-16 md:order-none md:mx-0 md:mb-40 md:bg-transparent md:p-0"
            >
              <InputText
                v-model="userInput"
                :title="
                  $texts('storefinder.searchText', 'Search by city, name')
                "
                icon-end="search"
              />
            </div>
            <div class="storefinder-pills-container overflow-auto">
              <div class="flex pl-16 md:flex-wrap md:pl-0">
                <div
                  v-for="(filter, index) in filters"
                  :key="index"
                  class="pb-16 pr-6 md:pb-0 md:pr-0"
                >
                  <NuxtLink
                    :to="filter.to"
                    class="button md:mb-16 md:mr-16"
                    :class="{
                      'border-black': filter.active,
                      'is-disabled pointer-events-none': !filter.enabled,
                    }"
                    >{{ filter.label }}</NuxtLink
                  >
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        ref="resultsListContainer"
        class="max-h-svh grow overflow-y-auto overflow-x-hidden bg-grey-light-03 lg:block lg:w-[28%] lg:shrink-0 lg:grow-0"
        :class="{
          'md:hidden': activeStoreIdFromParam,
        }"
      >
        <StoreFinderResults
          v-if="data?.items?.length"
          v-bind="data"
          :class="{ 'opacity-50': isLoading }"
          :active-store-id="
            isMobileOrTablet ? activeStoreIdFromParam : activeStoreId
          "
          :active-group-id="activeGroupId"
        />
        <div v-else class="p-24">
          {{
            $texts(
              'storefinder.noResultsMessage',
              'Unfortunately no stores were found for your search.',
            )
          }}
        </div>
      </div>

      <div
        class="w-full lg:block"
        :class="{
          hidden: activeStoreIdFromParam === undefined,
        }"
      >
        <StoreFinderDetail
          v-if="activeStoreId"
          :id="activeStoreId"
          :key="activeStoreId"
          ref="storeFinderDetail"
          class="shrink grow mobile-only:fixed mobile-only:right-0 mobile-only:top-0 mobile-only:z-storefinder-detail-mobile"
        />
      </div>
    </div>
    <BlokkliField v-if="node" name="field_paragraphs" :list="node.paragraphs" />
  </BlokkliProvider>
</template>

<script lang="ts" setup>
import type { RouteLocationRaw } from 'vue-router'
import {
  disableBodyScroll,
  enableBodyScroll,
  clearAllBodyScrollLocks,
} from 'body-scroll-lock'
import type { UseHeadInput, ActiveHeadEntry, MergeHead } from '@unhead/vue'
import { DistributorType } from '#graphql-operations'

const { $texts } = useNuxtApp()
const container = ref<HTMLDivElement | null>()

const { isMobile, isMobileOrTablet } = useViewport()

definePageMeta({
  path: '/:prefix/storefinder/:id?',
  name: 'storefinder',
  staticDrupalPage: 'store_finder',
  key: (route) =>
    (route.params.prefix || '').toString() +
    (route.params.name || '').toString(),

  scrollToTop: (to, from) => {
    // When navigating inside the storefinder don't scroll to the top.
    if (from.name === 'storefinder' && to.name === 'storefinder') {
      return false
    }
    return true
  },
})

let inputTimeout: number | undefined

const route = useRoute()
const router = useRouter()
const text = useQueryString('text')
const typeValue = useQueryString('type')
const userInput = ref(text.value)

const type = computed<DistributorType | undefined>(() => {
  if (typeValue.value === 'own_boutique') {
    return DistributorType.Own_boutique
  } else if (typeValue.value === 'service_center') {
    return DistributorType.Service_center
  } else if (typeValue.value === 'store') {
    return DistributorType.Store
  }
})

const { data, status } = await useAsyncData(
  async () => {
    const v = await useCachedGraphqlQuery('distributorSearch', {
      text: text.value,
      type: type.value,
    })

    return v.data.distributorSearch
  },
  {
    watch: [type, text],
  },
)

const activeStoreIdFromParam = computed(() => {
  if (route.params.id && typeof route.params.id === 'string') {
    return route.params.id.match(/^\d+/)?.[0]
  }
})

const activeStoreId = computed(
  () => activeStoreIdFromParam.value || data.value?.items?.[0]?.id,
)

const activeGroupId = computed(() => {
  if (route.query.groupId && typeof route.query.groupId === 'string') {
    return route.query.groupId
  }

  return undefined
})

const { data: query } = await useAsyncData('store_finder', () =>
  useCachedGraphqlQuery('storefinderRoute').then((v) => v.data),
)

const node = computed(() => query.value?.getStaticNode)

let headMetatagsEntry: void | ActiveHeadEntry<UseHeadInput<MergeHead>>
const updateMetatags = async () => {
  headMetatagsEntry?.dispose()

  if (activeStoreIdFromParam.value) {
    const { data: hrefLangQuery } = await useAsyncData(
      'hreflangsForNonDrupalRoute',
      () =>
        useCachedGraphqlQuery('hreflangsForNonDrupalRoute', {
          pathWithPrefix: route.fullPath,
        }).then((v) => v.data),
    )

    const metatags = buildDrupalMetatags(hrefLangQuery.value?.metatags)

    headMetatagsEntry = useHead(metatags)
  } else {
    const metatags = buildDrupalMetatags(node.value?.metatags?.metatags)
    headMetatagsEntry = useHead(metatags)
  }
}

await updateMetatags() // we need await. Otherwise updateMetatags does not have a nuxt context and cannot correctly call useHead. Will result in the error: A composable that requires access to the Nuxt instance was called outside of a plugin

watch(() => route.fullPath, updateMetatags)

onBeforeUnmount(() => {
  headMetatagsEntry?.dispose()
})

const { getDistributorTypeLabel } = await useStoreFinder()

watch(userInput, (v) => {
  window.clearTimeout(inputTimeout)

  inputTimeout = window.setTimeout(() => {
    router.push({
      name: route.name || undefined,
      query: {
        text: v || undefined,
      },
    })
  }, 400)
})

const isLoading = computed(
  () => status.value === 'pending' || text.value !== userInput.value,
)

type StoreFinderFilter = {
  label: string
  type: DistributorType | undefined
  active: boolean
  enabled: boolean
  to: RouteLocationRaw
}

const filters = computed<StoreFinderFilter[]>(() => {
  return [
    {
      label: $texts('storefinder.all', 'All'),
      type: undefined,
    },
    {
      type: DistributorType.Own_boutique,
    },
    {
      type: DistributorType.Store,
    },
    {
      type: DistributorType.Service_center,
    },
  ].map((v) => {
    const enabled = isFilterEnabled(v.type)
    const to: RouteLocationRaw = {
      name: route.name || undefined,
      query: {
        ...route.query,
        type: v.type ? v.type.toString() : undefined,
      },
    }

    // If we change this filter the groupId should be reset.
    delete to.query?.groupId

    return {
      ...v,
      label: v.type ? getDistributorTypeLabel(v.type) : v.label,
      active: type.value === v.type,
      enabled,
      to,
    }
  })
})

const isFilterEnabled = (type?: DistributorType): boolean => {
  if (type === DistributorType.Own_boutique) {
    return !!data.value?.countBoutique
  } else if (type === DistributorType.Store) {
    return !!data.value?.countStore
  } else if (type === DistributorType.Service_center) {
    return !!data.value?.countServiceCenter
  }

  return true
}

useRouteCache((ctx) => ctx.setMaxAge(30 * 60).setCacheable())

const setContainerHeight = () => {
  if (!container.value) {
    return
  }

  container.value.style.height = `calc(100dvh - ${container.value.getBoundingClientRect().top + (isMobile.value ? 40 : 0)}px)`
}

watch(container, setContainerHeight)
watch(isMobile, setContainerHeight)

const resultsListContainer = ref<HTMLUListElement>()

const updateScrollFade = () => {
  if (resultsListContainer.value) {
    const element = resultsListContainer.value

    const topFadeHeight = Math.min(element.scrollTop / 10, 100)
    const bottomFadeHeight = Math.min(
      (element.scrollHeight - element.clientHeight - element.scrollTop) / 10,
      100,
    )

    element.style.maskImage = `linear-gradient(to bottom, transparent 0%, black ${topFadeHeight}px, black calc(100% - ${bottomFadeHeight}px), transparent 100%)`
  }
}

onMounted(() => {
  // need nextTick here since otherwise the scroll fade is not rendered on page load.
  nextTick(updateScrollFade)
})
watch(resultsListContainer, () => {
  resultsListContainer.value?.addEventListener('scroll', updateScrollFade)
})

const storeFinderDetail = ref<HTMLElement>()
watch([activeStoreIdFromParam, isMobile, storeFinderDetail], () => {
  if (!storeFinderDetail.value) {
    return
  }

  if (activeStoreIdFromParam.value && isMobile.value) {
    disableBodyScroll(storeFinderDetail.value, {
      allowTouchMove: () => true,
    })
  } else {
    enableBodyScroll(storeFinderDetail.value)
  }
})

router.afterEach(() => clearAllBodyScrollLocks())

const { trackPageView } = useAnalyticsTracking()
trackPageView()

const isRebranding = await useFeatureFlag()
</script>

<!-- eslint-disable-next-line vue/enforce-style-attribute // Disabled on linter introduction. Feel free to fix. -->
<style lang="postcss">
.storefinder-pills-container {
  scrollbar-width: none;
}

:deep(.paragraph),
.paragraph {
  @include paragraph-spacing;
}
</style>
